import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { WorkScopeLoadingScreen } from "@/components/shared/WorkScopeLoadingScreen";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  useProjectScheduleBlocks,
  useTeamMembers,
} from "@/hooks/use-app-queries";
import {
  createProjectPayment,
  downloadFinalInvoicePdf,
  fetchProjectById,
  sendFinalInvoiceToClient,
  updateProject,
  type ProjectFinalInvoiceSummary,
  type ProjectPaymentMethod,
  type ProjectStatus,
} from "@/lib/projects-clients";
import { sendProjectPaymentFollowup } from "@/lib/billing-exports";
import { useToast } from "@/hooks/use-toast";
import {
  ArrowLeft,
  ChevronDown,
  Download,
  Eye,
  Loader2,
  Send,
} from "lucide-react";
import { SendPaymentFollowUpDialog } from "@/components/projects/SendPaymentFollowUpDialog";
import { GenerateFinalInvoiceDialog } from "@/components/projects/GenerateFinalInvoiceDialog";
import { FinalInvoicePreviewDialog } from "@/components/projects/FinalInvoicePreviewDialog";
import { useAuth } from "@/auth/auth-context";
import type { ApprovalDeliveryMethod } from "@/lib/tickets";

type ProjectDetailResponse = {
  id: string;
  name: string;
  code?: string | null;
  createdAt: string;
  archivedAt?: string | null;
  jobSiteAddress?: string | null;
  status?: ProjectStatus;
  approvedTotal?: number;
  pendingApprovalTicketCount?: number;
  paidTotal?: number;
  balance?: number;
  paymentStatus?: "NO_APPROVED_TOTAL" | "UNPAID" | "PARTIALLY_PAID" | "PAID";
  isActive: boolean;
  assignedMember?: {
    id: string;
    fullName: string;
    email: string;
    role?: string;
  } | null;
  client?: {
    id: string;
    name: string;
    companyName?: string | null;
    email?: string | null;
    phone?: string | null;
  } | null;
  tickets: Array<{
    id: string;
    ticketNumber: string;
    type: "TM_TICKET" | "CHANGE_ORDER" | "EXTRA_WORK";
    title?: string | null;
    status: string;
    totalAmount: string;
    createdAt: string;
    deletedAt?: string | null;
    deleteReason?: string | null;
  }>;
  internalEstimateTickets?: ProjectInternalEstimateTicket[];
  finalInvoice?: ProjectFinalInvoiceSummary | null;
};

type PricingCategory =
  | "labor"
  | "materials"
  | "equipment"
  | "subcontractor"
  | "other";
type CustomerDisplayMode =
  | "final_price_only"
  | "summary_breakdown"
  | "full_itemized_breakdown";

type InternalEstimateRow = {
  id?: string;
  category?: PricingCategory | string | null;
  description?: string | null;
  quantity?: number | string | null;
  unit?: string | null;
  unitCost?: number | string | null;
  lineTotal?: number | string | null;
  internalNote?: string | null;
};

type PricingMetadata = {
  pricingVersion?: number;
  customerDisplayMode?: CustomerDisplayMode;
  customerPrice?: number;
  discountAmount?: number;
  taxAmount?: number;
  finalAmount?: number;
  internalEstimate?: {
    rows?: InternalEstimateRow[];
    internalSubtotal?: number;
    customerFacingFinalPrice?: number;
  } | null;
};

type ProjectInternalEstimateTicket = {
  id: string;
  ticketNumber: string;
  type: "TM_TICKET" | "CHANGE_ORDER" | "EXTRA_WORK";
  title?: string | null;
  status: string;
  totalAmount: string | number;
  subtotalAmount?: string | number | null;
  taxAmount?: string | number | null;
  createdAt: string;
  internalNotes?: string | null;
};

const PAYMENT_STATUS_LABEL: Record<
  NonNullable<ProjectDetailResponse["paymentStatus"]>,
  string
> = {
  NO_APPROVED_TOTAL: "—",
  UNPAID: "Unpaid",
  PARTIALLY_PAID: "Partially Paid",
  PAID: "Paid",
};

function resolvePaymentStatusLabel(
  project: Pick<
    ProjectDetailResponse,
    "paymentStatus" | "pendingApprovalTicketCount"
  >,
) {
  if (
    (project.paymentStatus ?? "NO_APPROVED_TOTAL") === "NO_APPROVED_TOTAL" &&
    Number(project.pendingApprovalTicketCount ?? 0) > 0
  ) {
    return `Pending (${Number(project.pendingApprovalTicketCount ?? 0)})`;
  }

  return PAYMENT_STATUS_LABEL[project.paymentStatus ?? "NO_APPROVED_TOTAL"];
}

function paymentChipClass(status?: ProjectDetailResponse["paymentStatus"]) {
  switch (status) {
    case "UNPAID":
      return "bg-destructive/10 text-destructive";
    case "PARTIALLY_PAID":
      return "bg-warning/20 text-warning";
    case "PAID":
      return "bg-status-approved-bg text-status-approved";
    case "NO_APPROVED_TOTAL":
    default:
      return "bg-muted text-muted-foreground";
  }
}

function formatCurrency(value?: number) {
  const amount = Number(value ?? 0);
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

function formatCompactNumber(value: number) {
  if (!Number.isFinite(value)) return "";
  return Number.isInteger(value)
    ? String(value)
    : value.toFixed(2).replace(/\.?0+$/, "");
}

function formatBalance(value?: number) {
  const amount = Number(value ?? 0);
  if (amount < 0) {
    return `Overpaid ${formatCurrency(Math.abs(amount))}`;
  }
  return formatCurrency(amount);
}

const PROJECT_STATUSES: ProjectStatus[] = [
  "ACTIVE",
  "ON_HOLD",
  "COMPLETED",
  "CANCELLED",
];
const STATUS_LABEL: Record<ProjectStatus, string> = {
  ACTIVE: "Active",
  ON_HOLD: "On Hold",
  COMPLETED: "Completed",
  CANCELLED: "Cancelled",
  ARCHIVED: "Archived",
};

const PAYMENT_METHOD_OPTIONS: Array<{
  value: ProjectPaymentMethod;
  label: string;
}> = [
  { value: "CASH", label: "Cash" },
  { value: "CHECK", label: "Check" },
  { value: "CARD", label: "Card" },
  { value: "ZELLE", label: "Zelle" },
  { value: "ACH", label: "ACH" },
  { value: "OTHER", label: "Other" },
];

function ticketTypeLabel(type: "TM_TICKET" | "CHANGE_ORDER" | "EXTRA_WORK") {
  if (type === "TM_TICKET") return "Estimate";
  if (type === "CHANGE_ORDER") return "Change Order";
  return "Extra Work";
}

function ticketStatusClass(status: string) {
  switch (status) {
    case "APPROVED":
      return "bg-status-approved-bg text-status-approved";
    case "REJECTED":
      return "bg-destructive/10 text-destructive";
    case "NEEDS_CLARIFICATION":
      return "bg-primary/10 text-primary";
    case "PENDING_APPROVAL":
      return "bg-warning/20 text-warning";
    case "DRAFT":
      return "bg-slate-100 text-slate-700";
    default:
      return "bg-slate-100 text-slate-700";
  }
}

function parsePricingMetadata(value?: string | null): PricingMetadata | null {
  if (!value?.trim()) return null;
  try {
    const parsed = JSON.parse(value) as unknown;
    if (!parsed || typeof parsed !== "object") return null;
    const record = parsed as PricingMetadata;
    return typeof record.pricingVersion === "number" ? record : null;
  } catch {
    return null;
  }
}

function formatPricingCategoryLabel(value?: string | null) {
  const normalized = String(value ?? "").toLowerCase();
  if (normalized.includes("labor")) return "Labor";
  if (normalized.includes("material")) return "Materials";
  if (normalized.includes("equipment")) return "Equipment";
  if (normalized.includes("subcontract")) return "Subcontractor";
  return "Other";
}

function formatPricingUnitLabel(value?: string | null) {
  const normalized = String(value ?? "").toLowerCase();
  if (["hour", "hours", "hr", "hrs"].includes(normalized)) return "hrs";
  if (["sq_ft", "sqft", "sf"].includes(normalized)) return "sq ft";
  if (["each", "unit", "units"].includes(normalized)) return "ea";
  if (normalized === "fixed") return "fixed";
  return value ?? "";
}

function formatCustomerDisplayMode(value?: string | null) {
  if (value === "summary_breakdown") return "Summary breakdown";
  if (value === "full_itemized_breakdown") return "Full itemized breakdown";
  return "Final price only";
}

function scheduleStatusClass(status: string) {
  switch (status) {
    case "DONE":
      return "bg-status-approved-bg text-status-approved";
    case "IN_PROGRESS":
      return "bg-primary/10 text-primary";
    case "PAUSED":
      return "bg-warning/20 text-warning";
    case "CANCELED":
      return "bg-destructive/10 text-destructive";
    case "MISSED":
      return "bg-slate-200 text-slate-700";
    case "SCHEDULED":
    default:
      return "bg-accent/10 text-accent";
  }
}

function formatDateTime(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function formatTimeOnly(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });
}

function assignedMemberLabel(project: ProjectDetailResponse) {
  return (
    project.assignedMember?.fullName?.trim() ||
    project.assignedMember?.email?.trim() ||
    ""
  );
}

export default function ProjectDetail() {
  const { user } = useAuth();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [loading, setLoading] = useState(true);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [project, setProject] = useState<ProjectDetailResponse | null>(null);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentDate, setPaymentDate] = useState(() =>
    new Date().toISOString().slice(0, 10),
  );
  const [paymentMethod, setPaymentMethod] =
    useState<ProjectPaymentMethod>("CASH");
  const [paymentNote, setPaymentNote] = useState("");
  const [savingPayment, setSavingPayment] = useState(false);
  const [sendingPaymentFollowup, setSendingPaymentFollowup] = useState(false);
  const [paymentFollowupDialogOpen, setPaymentFollowupDialogOpen] =
    useState(false);
  const [assignmentModalOpen, setAssignmentModalOpen] = useState(false);
  const [assignmentMemberId, setAssignmentMemberId] = useState("unassigned");
  const [savingAssignment, setSavingAssignment] = useState(false);
  const [finalInvoiceDialogOpen, setFinalInvoiceDialogOpen] = useState(false);
  const [finalInvoicePreviewInvoice, setFinalInvoicePreviewInvoice] =
    useState<ProjectFinalInvoiceSummary | null>(null);
  const [loadingFinalInvoicePdf, setLoadingFinalInvoicePdf] = useState<
    "download" | null
  >(null);
  const [sendingFinalInvoice, setSendingFinalInvoice] = useState(false);
  const [expandedInternalEstimateId, setExpandedInternalEstimateId] = useState<
    string | null
  >(null);

  const loadProject = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const data = await fetchProjectById(id);
      setProject(data as ProjectDetailResponse);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load project.");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void loadProject();
  }, [loadProject]);

  const scheduleBlocksQuery = useProjectScheduleBlocks(
    project?.id ?? null,
    false,
    Boolean(project?.id),
  );
  const canManageAssignments =
    user?.currentPlan === "TEAM" && user.role === "ADMIN";
  const canGenerateFinalInvoice = user?.role === "ADMIN" || user?.role === "PM";
  const teamMembersQuery = useTeamMembers(
    Boolean(assignmentModalOpen && canManageAssignments),
  );
  const activeTeamMembers = useMemo(
    () =>
      (teamMembersQuery.data ?? []).filter(
        (member) =>
          member.kind === "user" &&
          member.status === "ACTIVE" &&
          member.isActive,
      ),
    [teamMembersQuery.data],
  );
  const internalEstimateTickets = useMemo(() => {
    return (project?.internalEstimateTickets ?? [])
      .map((ticket) => {
        const metadata = parsePricingMetadata(ticket.internalNotes);
        const rows = (metadata?.internalEstimate?.rows ?? []).filter((row) =>
          row.description?.trim(),
        );
        if (!metadata || rows.length === 0) return null;
        const internalTotal = rows.reduce((sum, row) => {
          const rowTotal = Number(row.lineTotal ?? 0);
          return Number.isFinite(rowTotal) ? sum + rowTotal : sum;
        }, 0);
        const categoryTotals = [
          "Labor",
          "Materials",
          "Equipment",
          "Subcontractor",
          "Other",
        ]
          .map((label) => {
            const amount = rows.reduce((sum, row) => {
              if (formatPricingCategoryLabel(row.category) !== label)
                return sum;
              const rowTotal = Number(row.lineTotal ?? 0);
              return Number.isFinite(rowTotal) ? sum + rowTotal : sum;
            }, 0);
            return { label, amount };
          })
          .filter(({ amount }) => Math.abs(amount) > 0.005);

        return {
          ticket,
          metadata,
          rows,
          internalTotal,
          categoryTotals,
        };
      })
      .filter(
        (
          item,
        ): item is {
          ticket: ProjectInternalEstimateTicket;
          metadata: PricingMetadata;
          rows: InternalEstimateRow[];
          internalTotal: number;
          categoryTotals: Array<{ label: string; amount: number }>;
        } => Boolean(item),
      )
      .sort(
        (a, b) =>
          new Date(b.ticket.createdAt).getTime() -
          new Date(a.ticket.createdAt).getTime(),
      );
  }, [project?.internalEstimateTickets]);

  if (loading) return <WorkScopeLoadingScreen />;
  if (error || !project)
    return (
      <div className="text-sm text-destructive">
        {error ?? "Project not found"}
      </div>
    );

  const changeStatus = async (status: ProjectStatus) => {
    setUpdatingStatus(true);
    try {
      const updated = await updateProject(project.id, { status });
      setProject((prev) => (prev ? { ...prev, status: updated.status } : prev));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to update status.");
    } finally {
      setUpdatingStatus(false);
    }
  };

  const openAssignmentModal = () => {
    setAssignmentMemberId(project.assignedMember?.id ?? "unassigned");
    setAssignmentModalOpen(true);
  };

  const closeAssignmentModal = () => {
    if (savingAssignment) return;
    setAssignmentModalOpen(false);
    setAssignmentMemberId("unassigned");
  };

  const saveAssignment = async () => {
    setSavingAssignment(true);
    try {
      await updateProject(project.id, {
        assignedMemberId:
          assignmentMemberId === "unassigned" ? "" : assignmentMemberId,
      });
      toast({ title: "Project assignment updated" });
      setAssignmentModalOpen(false);
      setAssignmentMemberId("unassigned");
      await loadProject();
    } catch (e) {
      toast({
        title: "Could not update assignment",
        description: e instanceof Error ? e.message : "Please try again.",
        variant: "destructive",
      });
    } finally {
      setSavingAssignment(false);
    }
  };

  const openRecordPaymentModal = () => {
    setPaymentAmount("");
    setPaymentDate(new Date().toISOString().slice(0, 10));
    setPaymentMethod("CASH");
    setPaymentNote("");
    setPaymentModalOpen(true);
  };

  const submitPayment = async () => {
    if (!project) return;
    const amount = Number(paymentAmount || 0);
    if (!Number.isFinite(amount) || amount <= 0) {
      setError("Amount must be greater than 0.");
      return;
    }
    if (!paymentDate) {
      setError("Payment date is required.");
      return;
    }
    setSavingPayment(true);
    try {
      await createProjectPayment(project.id, {
        amount,
        paymentDate,
        method: paymentMethod,
        note: paymentNote.trim() || undefined,
      });
      setPaymentModalOpen(false);
      await loadProject();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to record payment.");
    } finally {
      setSavingPayment(false);
    }
  };

  const followUpPayment = async (
    deliveryMethod: ApprovalDeliveryMethod,
    smsConsentConfirmed?: boolean,
  ) => {
    if (!project) return;
    setSendingPaymentFollowup(true);
    try {
      const result = await sendProjectPaymentFollowup(project.id, {
        deliveryMethod,
        smsConsentConfirmed,
      });
      const sentLabel =
        result.sentChannels.length === 2
          ? "Email and SMS sent"
          : result.sentChannels[0] === "sms"
            ? "SMS sent"
            : "Email sent";
      const failureLabel = result.failedChannels.length
        ? ` Failed: ${result.failedChannels.map((item) => item.channel.toUpperCase()).join(", ")}.`
        : "";
      toast({
        title: "Payment follow-up sent",
        description: `${sentLabel}.${failureLabel}`,
      });
      setPaymentFollowupDialogOpen(false);
      await loadProject();
    } catch (e) {
      setError(
        e instanceof Error ? e.message : "Failed to send payment follow-up.",
      );
      toast({
        title: "Payment follow-up failed",
        description:
          e instanceof Error ? e.message : "Failed to send payment follow-up.",
        variant: "destructive",
      });
    } finally {
      setSendingPaymentFollowup(false);
    }
  };

  const openFinalInvoicePreview = () => {
    if (!project?.finalInvoice) return;
    setFinalInvoicePreviewInvoice(project.finalInvoice);
  };

  const downloadFinalInvoice = async () => {
    if (!project?.finalInvoice) return;
    setLoadingFinalInvoicePdf("download");
    try {
      const { blob, fileName } = await downloadFinalInvoicePdf(
        project.id,
        project.finalInvoice.id,
      );
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = fileName;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast({
        title: "Could not download final invoice",
        description: e instanceof Error ? e.message : "Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoadingFinalInvoicePdf(null);
    }
  };

  const sendFinalInvoice = async () => {
    if (!project?.finalInvoice || !project.client?.email?.trim()) return;
    setSendingFinalInvoice(true);
    try {
      const updated = await sendFinalInvoiceToClient(
        project.id,
        project.finalInvoice.id,
      );
      toast({
        title: "Final invoice sent",
        description: `Sent to ${updated.sentToEmail ?? project.client.email}.`,
      });
      await loadProject();
    } catch (e) {
      toast({
        title: "Could not send final invoice",
        description: e instanceof Error ? e.message : "Please try again.",
        variant: "destructive",
      });
    } finally {
      setSendingFinalInvoice(false);
    }
  };

  const hasOutstandingBalance = Number(project.balance ?? 0) > 0;
  const isArchivedProject = Boolean(project.archivedAt);

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-start gap-3">
        <button
          onClick={() => navigate("/projects")}
          className="p-2 rounded-md hover:bg-secondary"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="flex-1">
          <h1 className="text-xl font-bold text-[#0C1F4F]">{project.name}</h1>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate(`/tickets/new?projectId=${project.id}`)}
        >
          New Ticket
        </Button>
      </div>

      <div className="rounded-lg border bg-card p-5 space-y-3">
        <h2 className="font-semibold text-[#0C1F4F]">Project Summary</h2>
        <div className="grid sm:grid-cols-2 gap-3 text-sm">
          <p>
            <span className="text-muted-foreground">Client:</span>{" "}
            {project.client ? (
              <span>
                <button
                  className="text-accent hover:underline"
                  onClick={() => navigate(`/clients/${project.client?.id}`)}
                >
                  {project.client.name}
                </button>
                {project.client.companyName ? (
                  <span className="mt-0.5 block text-xs text-muted-foreground">
                    {project.client.companyName}
                  </span>
                ) : null}
              </span>
            ) : (
              "No client"
            )}
          </p>
          <p>
            <span className="text-muted-foreground">Project Code:</span>{" "}
            {project.code ?? "Auto"}
          </p>
          <p>
            <span className="text-muted-foreground">Created at:</span>{" "}
            {formatDateTime(project.createdAt)}
          </p>
          <p>
            <span className="text-muted-foreground">Address:</span>{" "}
            {project.jobSiteAddress ?? "No address"}
          </p>
          {user?.currentPlan === "TEAM" ? (
            <div className="flex items-center gap-2">
              <p>
                <span className="text-muted-foreground">Assigned to:</span>{" "}
                <span className="font-medium text-[#0C1F4F]">
                  {assignedMemberLabel(project) || "Unassigned"}
                </span>
              </p>
              {canManageAssignments ? (
                <button
                  type="button"
                  className="text-xs font-semibold text-accent hover:underline"
                  onClick={openAssignmentModal}
                >
                  {project.assignedMember?.id ? "Change" : "Assign"}
                </button>
              ) : null}
            </div>
          ) : null}
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground">Status:</span>
            <select
              className="h-8 rounded-md border bg-background px-2 text-sm"
              value={project.status ?? "ACTIVE"}
              onChange={(e) =>
                void changeStatus(e.target.value as ProjectStatus)
              }
              disabled={updatingStatus}
            >
              {PROJECT_STATUSES.map((status) => (
                <option key={status} value={status}>
                  {STATUS_LABEL[status]}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="rounded-lg border bg-card overflow-hidden">
        <div className="border-b px-5 py-4">
          <h2 className="font-semibold text-[#0C1F4F]">
            Project Schedule Blocks
          </h2>
        </div>
        {scheduleBlocksQuery.isLoading && !scheduleBlocksQuery.data ? (
          <div className="p-5 text-sm text-muted-foreground">
            Loading schedule blocks...
          </div>
        ) : (scheduleBlocksQuery.data?.items?.length ?? 0) === 0 ? (
          <div className="p-5 text-sm text-muted-foreground">
            No schedule blocks for this project yet.
          </div>
        ) : (
          <>
            <div className="hidden md:block">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="text-left px-4 py-3 text-[#0C1F4F]">Date</th>
                    <th className="text-left px-4 py-3 text-[#0C1F4F]">
                      Start
                    </th>
                    <th className="text-left px-4 py-3 text-[#0C1F4F]">End</th>
                    <th className="text-left px-4 py-3 text-[#0C1F4F]">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {scheduleBlocksQuery.data?.items.map((block) => {
                    const startAt = new Date(block.startAt);
                    const endAt = new Date(block.endAt);
                    return (
                      <tr
                        key={block.id}
                        className="cursor-pointer border-b last:border-0 hover:bg-muted/20"
                        onClick={() =>
                          navigate(
                            `/schedule?date=${new Date(block.startAt).toISOString().slice(0, 10)}&blockId=${block.id}`,
                          )
                        }
                      >
                        <td className="px-4 py-3 font-medium">
                          {startAt.toLocaleDateString(undefined, {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                          })}
                        </td>
                        <td className="px-4 py-3">
                          {formatTimeOnly(block.startAt)}
                        </td>
                        <td className="px-4 py-3">
                          {formatTimeOnly(block.endAt)}
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${scheduleStatusClass(block.status)}`}
                          >
                            {block.status.replace(/_/g, " ")}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="space-y-3 p-4 md:hidden">
              {scheduleBlocksQuery.data?.items.map((block) => (
                <div
                  key={block.id}
                  className="cursor-pointer rounded-lg border bg-card p-4"
                  onClick={() =>
                    navigate(
                      `/schedule?date=${new Date(block.startAt).toISOString().slice(0, 10)}&blockId=${block.id}`,
                    )
                  }
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-[#0C1F4F]">
                        {new Date(block.startAt).toLocaleDateString(undefined, {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                        })}
                      </p>
                      <p className="mt-1 text-xs text-muted-foreground">
                        Start: {formatTimeOnly(block.startAt)}
                      </p>
                      <p className="mt-0.5 text-xs text-muted-foreground">
                        End: {formatTimeOnly(block.endAt)}
                      </p>
                    </div>
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${scheduleStatusClass(block.status)}`}
                    >
                      {block.status.replace(/_/g, " ")}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      <div className="rounded-lg border bg-card p-5 space-y-3">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <h2 className="font-semibold text-[#0C1F4F]">Payment Tracking</h2>
            <span
              className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${paymentChipClass(project.paymentStatus)}`}
            >
              {resolvePaymentStatusLabel(project)}
            </span>
          </div>
          {hasOutstandingBalance ? (
            <div className="flex flex-col sm:flex-row sm:items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setPaymentFollowupDialogOpen(true)}
              >
                Payment follow-up
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={openRecordPaymentModal}
              >
                Record a payment
              </Button>
            </div>
          ) : null}
        </div>
        <div className="space-y-3 text-sm">
          <p>
            <span className="text-muted-foreground">Approved Total:</span>{" "}
            <span className="font-medium">
              {formatCurrency(project.approvedTotal)}
            </span>
          </p>
          <p>
            <span className="text-muted-foreground">Paid:</span>{" "}
            <span className="font-medium">
              {formatCurrency(project.paidTotal)}
            </span>
          </p>
          <p>
            <span className="text-muted-foreground">Balance:</span>{" "}
            <span className="font-semibold text-accent">
              {formatBalance(project.balance)}
            </span>
          </p>
        </div>
      </div>

      <div className="rounded-lg border bg-card p-5 space-y-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="font-semibold text-[#0C1F4F]">Final Invoice</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Project-level invoice generated from approved tickets and recorded
              payments.
            </p>
          </div>
          {canGenerateFinalInvoice ? (
            <Button
              size="sm"
              className="bg-accent text-accent-foreground hover:bg-accent/90"
              onClick={() => setFinalInvoiceDialogOpen(true)}
            >
              {project.finalInvoice ? "Regenerate" : "Generate final invoice"}
            </Button>
          ) : null}
        </div>
        {!project.finalInvoice ? (
          <div className="rounded-lg border bg-muted/20 p-4 text-sm text-muted-foreground">
            No final invoice generated yet.
          </div>
        ) : (
          <div className="grid gap-4 lg:grid-cols-[1fr_auto]">
            <div className="grid gap-3 text-sm sm:grid-cols-2 lg:grid-cols-3">
              <p>
                <span className="block text-xs text-muted-foreground">
                  Invoice number
                </span>
                <span className="font-semibold text-[#0C1F4F]">
                  {project.finalInvoice.invoiceNumber}
                </span>
              </p>
              <p>
                <span className="block text-xs text-muted-foreground">
                  Generated
                </span>
                <span className="font-medium">
                  {formatDateTime(project.finalInvoice.generatedAt)}
                </span>
              </p>
              <p>
                <span className="block text-xs text-muted-foreground">
                  Generated by
                </span>
                <span className="font-medium">
                  {project.finalInvoice.generatedBy?.fullName ||
                    project.finalInvoice.generatedBy?.email ||
                    "—"}
                </span>
              </p>
              <p>
                <span className="block text-xs text-muted-foreground">
                  Approved total
                </span>
                <span className="font-medium">
                  {formatCurrency(project.finalInvoice.approvedTotal)}
                </span>
              </p>
              <p>
                <span className="block text-xs text-muted-foreground">
                  Payments received
                </span>
                <span className="font-medium">
                  {formatCurrency(project.finalInvoice.paymentsReceived)}
                </span>
              </p>
              <p>
                <span className="block text-xs text-muted-foreground">
                  {project.finalInvoice.balanceDue < 0
                    ? "Overpaid"
                    : "Balance due"}
                </span>
                <span className="font-semibold text-accent">
                  {project.finalInvoice.balanceDue < 0
                    ? formatCurrency(Math.abs(project.finalInvoice.balanceDue))
                    : formatCurrency(project.finalInvoice.balanceDue)}
                </span>
              </p>
              <p>
                <span className="block text-xs text-muted-foreground">
                  Detail level
                </span>
                <span className="font-medium">
                  {project.finalInvoice.detailLevel === "WITH_BREAKDOWNS"
                    ? "Includes ticket breakdowns"
                    : "Summary only"}
                </span>
              </p>
              <p>
                <span className="block text-xs text-muted-foreground">
                  Status
                </span>
                <span className="inline-flex rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
                  {project.finalInvoice.status === "SENT"
                    ? "Sent"
                    : "Generated"}
                </span>
              </p>
              {project.finalInvoice.sentAt ? (
                <p>
                  <span className="block text-xs text-muted-foreground">
                    Sent to
                  </span>
                  <span className="font-medium">
                    {project.finalInvoice.sentToEmail ?? "—"}
                  </span>
                </p>
              ) : null}
              {project.finalInvoice.sentAt ? (
                <p>
                  <span className="block text-xs text-muted-foreground">
                    Sent at
                  </span>
                  <span className="font-medium">
                    {formatDateTime(project.finalInvoice.sentAt)}
                  </span>
                </p>
              ) : null}
            </div>
            <div className="flex flex-wrap items-start gap-2 lg:justify-end">
              <Button
                size="sm"
                variant="outline"
                className="gap-2"
                onClick={openFinalInvoicePreview}
                disabled={loadingFinalInvoicePdf !== null}
              >
                <Eye className="h-4 w-4" />
                View PDF
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="gap-2"
                onClick={() => void downloadFinalInvoice()}
                disabled={loadingFinalInvoicePdf !== null}
              >
                <Download className="h-4 w-4" />
                Download PDF
              </Button>
              <Button
                size="sm"
                className="gap-2 bg-accent text-accent-foreground hover:bg-accent/90"
                onClick={() => void sendFinalInvoice()}
                disabled={sendingFinalInvoice || !project.client?.email?.trim()}
                title={
                  project.client?.email?.trim()
                    ? undefined
                    : "Client email is missing. Add an email to the client before sending the invoice."
                }
              >
                {sendingFinalInvoice ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
                {project.finalInvoice.status === "SENT"
                  ? "Resend to client"
                  : "Send to client"}
              </Button>
              {!project.client?.email?.trim() ? (
                <p className="basis-full text-xs text-amber-700 lg:text-right">
                  Client email is missing. Add an email to the client before
                  sending the invoice.
                </p>
              ) : null}
            </div>
          </div>
        )}
      </div>

      <div className="rounded-lg border bg-card overflow-hidden">
        <div className="border-b px-5 py-4">
          <h2 className="font-semibold text-[#0C1F4F]">Internal Estimates</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Saved estimate worksheets for this project.
          </p>
        </div>
        {internalEstimateTickets.length === 0 ? (
          <div className="p-5 text-sm text-muted-foreground">
            No internal estimates saved for this project yet.
          </div>
        ) : (
          <div className="divide-y">
            {internalEstimateTickets.map(
              ({ ticket, metadata, rows, internalTotal, categoryTotals }) => {
                const isExpanded = expandedInternalEstimateId === ticket.id;
                const customerPrice = Number(
                  metadata.customerPrice ??
                    ticket.subtotalAmount ??
                    ticket.totalAmount ??
                    0,
                );
                const discountAmount = Number(metadata.discountAmount ?? 0);
                const taxAmount = Number(
                  metadata.taxAmount ?? ticket.taxAmount ?? 0,
                );
                const finalAmount = Number(
                  metadata.finalAmount ?? ticket.totalAmount ?? 0,
                );
                return (
                  <div key={ticket.id}>
                    <div className="flex flex-col gap-3 px-5 py-4 transition-colors hover:bg-muted/20 lg:flex-row lg:items-start lg:justify-between">
                      <button
                        type="button"
                        className="min-w-0 flex-1 text-left"
                        onClick={() =>
                          setExpandedInternalEstimateId((current) =>
                            current === ticket.id ? null : ticket.id,
                          )
                        }
                      >
                        <div className="flex flex-wrap items-center gap-2 text-sm">
                          <span className="font-semibold text-[#0C1F4F]">
                            {ticket.ticketNumber}
                          </span>
                          <span className="text-muted-foreground">·</span>
                          <span className="font-medium text-accent">
                            {ticketTypeLabel(ticket.type)}
                          </span>
                          <span
                            className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${ticketStatusClass(ticket.status)}`}
                          >
                            {ticket.status.replace(/_/g, " ")}
                          </span>
                        </div>
                        <p className="mt-1 truncate text-sm font-medium text-foreground">
                          {ticket.title || "Untitled ticket"}
                        </p>
                        <p className="mt-1 text-xs text-muted-foreground">
                          Internal estimate {formatCurrency(internalTotal)} ·
                          Customer price {formatCurrency(customerPrice)} ·{" "}
                          {formatCustomerDisplayMode(
                            metadata.customerDisplayMode,
                          )}
                        </p>
                        <p className="mt-1 text-xs text-muted-foreground">
                          Created {formatDateTime(ticket.createdAt)}
                        </p>
                      </button>
                      <div className="flex shrink-0 items-center gap-2">
                        <span className="text-sm font-semibold text-accent">
                          {formatCurrency(finalAmount)}
                        </span>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => navigate(`/tickets/${ticket.id}`)}
                        >
                          Open ticket
                        </Button>
                        <button
                          type="button"
                          aria-label={
                            isExpanded ? "Collapse estimate" : "Expand estimate"
                          }
                          onClick={() =>
                            setExpandedInternalEstimateId((current) =>
                              current === ticket.id ? null : ticket.id,
                            )
                          }
                          className="rounded-md p-1 hover:bg-muted"
                        >
                          <ChevronDown
                            className={`h-4 w-4 text-muted-foreground transition-transform ${
                              isExpanded ? "rotate-180" : ""
                            }`}
                          />
                        </button>
                      </div>
                    </div>
                    {isExpanded ? (
                      <div className="space-y-4 border-t bg-muted/10 px-5 py-4">
                        <div className="overflow-x-auto rounded-lg border bg-background">
                          <table className="w-full min-w-[760px] text-sm">
                            <thead>
                              <tr className="border-b bg-muted/50">
                                <th className="px-4 py-3 text-left text-[#0C1F4F]">
                                  Description
                                </th>
                                <th className="px-4 py-3 text-left text-[#0C1F4F]">
                                  Category
                                </th>
                                <th className="px-4 py-3 text-right text-[#0C1F4F]">
                                  Qty
                                </th>
                                <th className="px-4 py-3 text-left text-[#0C1F4F]">
                                  Unit
                                </th>
                                <th className="px-4 py-3 text-right text-[#0C1F4F]">
                                  Rate
                                </th>
                                <th className="px-4 py-3 text-right text-[#0C1F4F]">
                                  Total
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {rows.map((row, index) => {
                                const rowKey =
                                  row.id ?? `${row.description}-${index}`;
                                return (
                                  <Fragment key={rowKey}>
                                    <tr className="border-b align-top">
                                      <td className="px-4 py-3 font-medium text-[#0C1F4F]">
                                        {row.description}
                                      </td>
                                      <td className="px-4 py-3">
                                        {formatPricingCategoryLabel(
                                          row.category,
                                        )}
                                      </td>
                                      <td className="px-4 py-3 text-right">
                                        {formatCompactNumber(
                                          Number(row.quantity || 0),
                                        )}
                                      </td>
                                      <td className="px-4 py-3">
                                        {formatPricingUnitLabel(row.unit)}
                                      </td>
                                      <td className="px-4 py-3 text-right">
                                        {formatCurrency(
                                          Number(row.unitCost ?? 0),
                                        )}
                                      </td>
                                      <td className="px-4 py-3 text-right font-medium">
                                        {formatCurrency(
                                          Number(row.lineTotal ?? 0),
                                        )}
                                      </td>
                                    </tr>
                                    {row.internalNote ? (
                                      <tr className="border-b bg-muted/20">
                                        <td
                                          colSpan={6}
                                          className="px-4 pb-3 pt-0 text-xs text-muted-foreground"
                                        >
                                          <span className="font-semibold text-[#51698F]">
                                            Internal note:
                                          </span>{" "}
                                          {row.internalNote}
                                        </td>
                                      </tr>
                                    ) : null}
                                  </Fragment>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>

                        <div className="grid gap-3 lg:grid-cols-[1fr_1fr]">
                          <div className="rounded-lg border bg-background p-4 text-sm">
                            <h3 className="font-semibold text-[#0C1F4F]">
                              Estimate summary
                            </h3>
                            <div className="mt-2 flex flex-wrap gap-x-5 gap-y-2">
                              {categoryTotals.length ? (
                                categoryTotals.map(({ label, amount }) => (
                                  <span
                                    key={label}
                                    className="text-muted-foreground"
                                  >
                                    {label}{" "}
                                    <span className="font-medium text-foreground">
                                      {formatCurrency(amount)}
                                    </span>
                                  </span>
                                ))
                              ) : (
                                <span className="text-muted-foreground">
                                  No category totals.
                                </span>
                              )}
                            </div>
                            <p className="mt-3 font-bold text-[#0C1F4F]">
                              Internal estimate total{" "}
                              {formatCurrency(internalTotal)}
                            </p>
                          </div>

                          <div className="rounded-lg border bg-background p-4 text-sm">
                            <h3 className="font-semibold text-[#0C1F4F]">
                              Customer-facing summary
                            </h3>
                            <div className="mt-2 grid gap-2">
                              <p>
                                <span className="text-muted-foreground">
                                  Display mode:
                                </span>{" "}
                                <span className="font-medium text-foreground">
                                  {formatCustomerDisplayMode(
                                    metadata.customerDisplayMode,
                                  )}
                                </span>
                              </p>
                              <p>
                                <span className="text-muted-foreground">
                                  Customer price:
                                </span>{" "}
                                <span className="font-medium text-foreground">
                                  {formatCurrency(customerPrice)}
                                </span>
                              </p>
                              <p>
                                <span className="text-muted-foreground">
                                  Discount / Tax:
                                </span>{" "}
                                <span className="font-medium text-foreground">
                                  -{formatCurrency(discountAmount)} /{" "}
                                  {formatCurrency(taxAmount)}
                                </span>
                              </p>
                              <p>
                                <span className="text-muted-foreground">
                                  Total sent for approval:
                                </span>{" "}
                                <span className="font-bold text-accent">
                                  {formatCurrency(finalAmount)}
                                </span>
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : null}
                  </div>
                );
              },
            )}
          </div>
        )}
      </div>

      <div className="rounded-lg border bg-card overflow-hidden">
        {project.tickets.length === 0 ? (
          <div className="p-5 space-y-3">
            <h2 className="font-semibold text-[#0C1F4F]">Recent Tickets</h2>
            <p className="text-sm text-muted-foreground">
              No tickets for this project yet.
            </p>
            <Button
              size="sm"
              className="bg-accent text-accent-foreground hover:bg-accent/90"
              onClick={() => navigate(`/tickets/new?projectId=${project.id}`)}
            >
              Create first ticket
            </Button>
          </div>
        ) : (
          <>
            <div className="border-b px-5 py-4">
              <h2 className="font-semibold text-[#0C1F4F]">Recent Tickets</h2>
            </div>
            <div className="hidden md:block">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="text-left px-4 py-3 text-[#0C1F4F]">
                      Ticket #
                    </th>
                    <th className="text-left px-4 py-3 text-[#0C1F4F]">
                      Ticket Type
                    </th>
                    <th className="text-left px-4 py-3 text-[#0C1F4F]">
                      Project
                    </th>
                    <th className="text-left px-4 py-3 text-[#0C1F4F]">
                      Status
                    </th>
                    <th className="text-right px-4 py-3 text-[#0C1F4F]">
                      Value
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {project.tickets.map((ticket) => (
                    <tr
                      key={ticket.id}
                      className={`border-b last:border-0 ${isArchivedProject ? "bg-muted/10 text-muted-foreground" : "cursor-pointer hover:bg-muted/30"}`}
                      onClick={
                        isArchivedProject
                          ? undefined
                          : () => navigate(`/tickets/${ticket.id}`)
                      }
                    >
                      <td className="px-4 py-3 font-medium">
                        {ticket.ticketNumber}
                      </td>
                      <td className="px-4 py-3 font-semibold text-accent">
                        {ticketTypeLabel(ticket.type)}
                      </td>
                      <td className="px-4 py-3">{project.name}</td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${ticketStatusClass(ticket.status)}`}
                        >
                          {ticket.status.replace(/_/g, " ")}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right font-semibold text-accent">
                        ${Number(ticket.totalAmount).toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="space-y-3 p-4 md:hidden">
              {project.tickets.map((ticket) => (
                <div
                  key={ticket.id}
                  className={`w-full rounded-lg border bg-card p-4 text-left ${isArchivedProject ? "opacity-80" : "transition-colors hover:bg-muted/20"}`}
                  onClick={
                    isArchivedProject
                      ? undefined
                      : () => navigate(`/tickets/${ticket.id}`)
                  }
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold text-accent">
                        {ticketTypeLabel(ticket.type)}
                      </p>
                      <p className="mt-1 truncate text-xs text-muted-foreground">
                        {project.name}
                      </p>
                      <p className="mt-0.5 truncate text-xs text-muted-foreground">
                        {project.client?.name ?? "No client"}
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-2 shrink-0">
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${ticketStatusClass(ticket.status)}`}
                      >
                        {ticket.status.replace(/_/g, " ")}
                      </span>
                      <p className="text-sm font-semibold text-accent">
                        ${Number(ticket.totalAmount).toFixed(2)}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      <GenerateFinalInvoiceDialog
        projectId={project.id}
        projectName={project.name}
        open={finalInvoiceDialogOpen}
        onOpenChange={setFinalInvoiceDialogOpen}
        onGenerated={(invoice) => {
          setFinalInvoicePreviewInvoice(invoice);
          void loadProject();
        }}
      />

      <FinalInvoicePreviewDialog
        projectId={project.id}
        projectName={project.name}
        clientName={project.client?.name}
        clientEmail={project.client?.email}
        invoice={finalInvoicePreviewInvoice}
        open={Boolean(finalInvoicePreviewInvoice)}
        onOpenChange={(open) => {
          if (!open) setFinalInvoicePreviewInvoice(null);
        }}
        onSent={(invoice) => {
          setFinalInvoicePreviewInvoice(invoice);
          void loadProject();
        }}
      />

      <Dialog
        open={paymentModalOpen}
        onOpenChange={(open) => !savingPayment && setPaymentModalOpen(open)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Record Payment</DialogTitle>
            <DialogDescription>
              Add a payment for this project.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Amount ($)</Label>
                <Input
                  type="number"
                  min={0}
                  value={paymentAmount}
                  onChange={(event) => setPaymentAmount(event.target.value)}
                  placeholder="0.00"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Payment date</Label>
                <Input
                  type="date"
                  value={paymentDate}
                  onChange={(event) => setPaymentDate(event.target.value)}
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Method</Label>
              <select
                className="h-9 w-full rounded-md border bg-background px-3 text-sm"
                value={paymentMethod}
                onChange={(event) =>
                  setPaymentMethod(event.target.value as ProjectPaymentMethod)
                }
              >
                {PAYMENT_METHOD_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label>Note</Label>
              <Input
                value={paymentNote}
                onChange={(event) => setPaymentNote(event.target.value)}
                placeholder="Optional note"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setPaymentModalOpen(false)}
              disabled={savingPayment}
            >
              Cancel
            </Button>
            <Button
              onClick={submitPayment}
              disabled={savingPayment}
              className="bg-accent text-accent-foreground hover:bg-accent/90"
            >
              {savingPayment ? "Saving..." : "Record payment"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={assignmentModalOpen}
        onOpenChange={(open) => !open && closeAssignmentModal()}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#0C1F4F]">
              Change project assignment
            </DialogTitle>
            <DialogDescription>
              Update who is responsible for this project.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="rounded-lg border bg-muted/30 p-3">
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Project
              </p>
              <p className="mt-1 font-semibold text-[#0C1F4F]">
                {project.name}
              </p>
              <p className="mt-2 text-xs text-muted-foreground">
                Current assignee:{" "}
                <span className="font-medium text-[#0C1F4F]">
                  {assignedMemberLabel(project) || "Unassigned"}
                </span>
              </p>
            </div>

            <div className="space-y-1.5">
              <Label className="text-[#0C1F4F]">Assigned to</Label>
              <select
                className="h-10 w-full rounded-md border bg-background px-3 text-sm"
                value={assignmentMemberId}
                onChange={(event) => setAssignmentMemberId(event.target.value)}
                disabled={savingAssignment}
              >
                <option value="unassigned">Unassigned</option>
                {activeTeamMembers.map((member) => (
                  <option key={member.id} value={member.id}>
                    {(member.fullName?.trim() || member.email) +
                      ` - ${member.role}`}
                  </option>
                ))}
              </select>
              {!teamMembersQuery.isLoading && activeTeamMembers.length === 0 ? (
                <p className="text-xs text-muted-foreground">
                  No active team members available.
                </p>
              ) : null}
            </div>
          </div>

          <DialogFooter className="gap-2.5">
            <Button
              type="button"
              variant="outline"
              onClick={closeAssignmentModal}
              disabled={savingAssignment}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => void saveAssignment()}
              disabled={savingAssignment}
              className="bg-accent text-accent-foreground hover:bg-accent/90"
            >
              {savingAssignment ? "Saving..." : "Save assignment"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <SendPaymentFollowUpDialog
        open={paymentFollowupDialogOpen}
        onOpenChange={setPaymentFollowupDialogOpen}
        target={
          project
            ? {
                projectName: project.name,
                clientName: project.client?.name ?? null,
                clientEmail: project.client?.email ?? null,
                clientPhone: project.client?.phone ?? null,
              }
            : null
        }
        loading={sendingPaymentFollowup}
        smsAllowedByPlan={user?.currentPlan !== "STARTER"}
        onConfirm={followUpPayment}
      />
    </div>
  );
}
